import { baseApi } from "@/shared/api";
import type { ImportWorkerDraftDetailResponse } from "../types";

interface CreateDraftPayload {
  last_section: string;
  last_contract_tab: string;
  last_documents_tab: string;
  scalar_data: Record<string, unknown>;
}

interface UpdateDraftPayload extends CreateDraftPayload {
  id: number;
}

interface UploadDraftFilePayload {
  draftId: number;
  field_name: string;
  file: File;
  file_index?: number | null;
}

interface DeleteDraftFilePayload {
  draftId: number;
  field_name: string;
  file_index?: number | null;
}

export const importDraftsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createImportDraft: builder.mutation<ImportWorkerDraftDetailResponse, CreateDraftPayload>({
      query: (body) => ({
        url: "/api/hr/import-draft/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "ImportWorkerDrafts" as const, id: "LIST" },
        { type: "NGOWorkers" as const, id: "LIST" },
      ],
    }),
    updateImportDraft: builder.mutation<ImportWorkerDraftDetailResponse, UpdateDraftPayload>({
      query: ({ id, ...body }) => ({
        url: `/api/hr/import-draft/${id}/`,
        method: "PUT",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "ImportWorkerDrafts" as const, id: "LIST" },
        { type: "NGOWorkers" as const, id: "LIST" },
      ],
    }),
    getImportDraft: builder.query<ImportWorkerDraftDetailResponse, number>({
      query: (id) => ({
        url: `/api/hr/import-draft/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "ImportWorkerDrafts" as const, id }],
    }),
    uploadImportDraftFile: builder.mutation<void, UploadDraftFilePayload>({
      query: ({ draftId, field_name, file, file_index }) => {
        const fd = new FormData();
        fd.append("field_name", field_name);
        fd.append("file", file);
        if (file_index != null) fd.append("file_index", String(file_index));
        return {
          url: `/api/hr/import-draft/${draftId}/files/`,
          method: "POST",
          body: fd,
          credentials: "include" as const,
        };
      },
      invalidatesTags: [],
    }),
    deleteImportDraftFile: builder.mutation<void, DeleteDraftFilePayload>({
      query: ({ draftId, field_name, file_index }) => ({
        url: `/api/hr/import-draft/${draftId}/files/`,
        method: "DELETE",
        body: file_index != null ? { field_name, file_index } : { field_name },
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { draftId }) => [{ type: "ImportWorkerDrafts" as const, id: draftId }],
    }),
    deleteImportDraft: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/hr/import-draft/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "ImportWorkerDrafts" as const, id: "LIST" },
        { type: "NGOWorkers" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useCreateImportDraftMutation,
  useUpdateImportDraftMutation,
  useGetImportDraftQuery,
  useUploadImportDraftFileMutation,
  useDeleteImportDraftFileMutation,
  useDeleteImportDraftMutation,
} = importDraftsApi;
